/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {parseExtendedAttributes} from '@ion/core/src/services/ConnectUtils';
import {getDriverScores as inheritedScores} from '@ion/core/src/services/GroupProperties';
import {Account, GenericObject} from '@ion/core/src/types/entities';
import {DriverScoreField} from '@ion/dls/src/editors/DriverScorecardEditorUtils';

/**
 * The utility just returns the default driver scores that
 * tailor the scoring logic to fit my fleet.
 *
 * ```typescript
 *
 *  import {getDefaultDriverScore} from './DriverScoreUtil';
 *
 *  const defaultScore = getDefaultDriverScore();
 *
 * ```
 */
export const getDefaultDriverScore = () => ({
  [DriverScoreField.harshAcceleration]       : 10,
  [DriverScoreField.harshDeceleration]       : 15,
  [DriverScoreField.hardCornering]           : 15,
  [DriverScoreField.speedingOneToFour]       : 0,
  [DriverScoreField.speedingFiveToNine]      : 5,
  [DriverScoreField.speedingTenToFourteen]   : 20,
  [DriverScoreField.speedingFifteenPlus]     : 35,
  [DriverScoreField.nightTimeDriving]        : 0,
  [DriverScoreField.visionCollisionWarning]  : 0,
  [DriverScoreField.visionDistractedDriving] : 0,
  [DriverScoreField.visionTrafficViolation]  : 0,
  [DriverScoreField.visionLaneDrift]         : 0,
  [DriverScoreField.visionLaneDeparture]     : 0
});

/**
 * The utility function returns the driver scores for the account. If the account
 * has the score value from its extended attribute it sends or takes the value from it's
 * ancestor using inherited microservice or returns the default score.
 *
 * ```typescript
 *
 *  import {getDriverScores} from './DriverScoreUtil';
 *
 *  const score = getDriverScores(account);
 *
 * ```
 */
export const getDriverScores = (currentAccount: Account) => {
  const extAttr   = parseExtendedAttributes(currentAccount, true);
  const ownScores = extAttr?.[DriverScoreField.driverScoring];
  return {
    ...(getDefaultDriverScore() || {}),
    ...(ownScores || inheritedScores() || {})
  };
};

/**
 * The utility function returns the boolean value based on condition that the
 * extended attribute of the account has value or not.
 *
 * ```typescript
 *
 *  import {hasDriverScoreInAccount} from './DriverScoreUtil';
 *
 *  const hasScore = hasDriverScoreInAccount(account);
 *
 * ```
 */
export const hasDriverScoreInAccount = (account: Account) => (
  !!parseExtendedAttributes(account, true)?.[DriverScoreField.driverScoring]
);

/**
 * The utility function returns the driver scores that the format required
 * for the driver scorecard dashboard. This reformat teh actual scores into
 * section and category scores.
 *
 * ``typescript
 *
 *  import {constructDriverScore} from './DriverScoreUtil';
 *
 *  const constructedScore = constructDriverScore(scores);
 *
 * ```
 */
export const constructDriverScore = (score: GenericObject) => {
  return ({
    keyScoringIndicators : {
      [DriverScoreField.harshAcceleration] : score[DriverScoreField.harshAcceleration],
      [DriverScoreField.harshDeceleration] : score[DriverScoreField.harshDeceleration],
      [DriverScoreField.hardCornering]     : score[DriverScoreField.hardCornering]
    },
    speeding : {
      [DriverScoreField.speedingOneToFour]     : score[DriverScoreField.speedingOneToFour],
      [DriverScoreField.speedingFiveToNine]    : score[DriverScoreField.speedingFiveToNine],
      [DriverScoreField.speedingTenToFourteen] : score[DriverScoreField.speedingTenToFourteen],
      [DriverScoreField.speedingFifteenPlus]   : score[DriverScoreField.speedingFifteenPlus]
    }
  });
};
